import { useRef } from 'react';
import type { CSSProperties } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Branding } from '@cloudsign/shared';
import { z } from 'zod';
import { api } from '../../lib/api';
import { brandStyle } from '../../lib/branding';
import { t } from '../../lib/i18n';
import { CAPABILITIES_KEY, useCan, useCaps } from '../../lib/session';
import { ErrorLine } from '../../components/ErrorPanel';
import { SettingsEditor } from '../../components/SettingsEditor';
import { Button, Card, PageHeader } from '../../components/ui';

const BrandingResponse = Branding.extend({ logoUrl: z.string().nullable() });

function BrandingPanel({ values, canEdit }: { values: Record<string, unknown>; canEdit: boolean }) {
  const caps = useCaps();
  const qc = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const branding = useQuery({ queryKey: ['branding'], queryFn: () => api.get('/branding', { schema: BrandingResponse }) });
  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: ['branding'] });
    await qc.invalidateQueries({ queryKey: CAPABILITIES_KEY });
  };
  const upload = useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append('file', file);
      return api.post('/branding/logo', { form });
    },
    onSuccess: refresh,
  });
  const remove = useMutation({ mutationFn: () => api.delete('/branding/logo'), onSuccess: refresh });

  const b = (values['branding'] ?? {}) as Record<string, unknown>;
  const primary = String(b['primaryColour'] ?? '');
  const accent = String(b['accentColour'] ?? '');
  const displayName = String(b['displayName'] ?? '') || caps.tenant?.name || t('app.name');
  const logoUrl = branding.data?.logoUrl ?? null;
  const style = brandStyle({ primaryColour: primary, accentColour: accent });

  return (
    <Card title={t('settings.core.branding.logo')}>
      <div className="grid gap-6 md:grid-cols-2">
        <div className="space-y-3">
          <ErrorLine error={upload.error ?? remove.error} />
          <p className="text-sm text-slate-600">{t('settings.logoHelp')}</p>
          {canEdit ? (
            <div className="flex flex-wrap gap-2">
              <input
                ref={fileInput}
                type="file"
                accept="image/png,image/jpeg,image/svg+xml"
                className="sr-only"
                aria-label={t('settings.chooseLogo')}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) upload.mutate(f);
                  e.target.value = '';
                }}
              />
              <Button onClick={() => fileInput.current?.click()} disabled={upload.isPending}>
                {logoUrl ? t('settings.replaceLogo') : t('settings.uploadLogo')}
              </Button>
              {logoUrl ? (
                <Button variant="danger" onClick={() => remove.mutate()} disabled={remove.isPending}>
                  {t('settings.removeLogo')}
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
        <div>
          <p className="mb-1 text-sm font-medium">{t('settings.preview')}</p>
          <div style={style as CSSProperties} className="overflow-hidden rounded-md border border-slate-300" data-testid="branding-preview">
            <div className="flex items-center gap-3 px-4 py-3" style={{ background: 'var(--brand-primary)', color: 'var(--brand-on-primary)' }}>
              {logoUrl ? <img src={logoUrl} alt="" className="h-8 max-w-[8rem] object-contain" /> : null}
              <span className="font-semibold">{displayName}</span>
            </div>
            <div className="p-4">
              <span className="inline-block rounded-md px-3 py-1 text-sm font-medium" style={{ background: 'var(--brand-accent)', color: '#111827' }}>
                {t('settings.previewAccent')}
              </span>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}

export function SettingsPage() {
  const canEdit = useCan('account.manage');
  return (
    <>
      <PageHeader title={t('nav.settings')} description={t('nav.settings.desc')} />
      <SettingsEditor moduleKey="core" base="/settings" canEdit={canEdit} above={(values) => <BrandingPanel values={values} canEdit={canEdit} />} />
    </>
  );
}
